import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';

// Redux
import { setOrderDataAction } from '../../redux/Actions/CreateOrderActions';
import { createNewOrder, createNewSubscriptions } from '../../redux/Reducers/CreateOrderReducer';

// Utils
import { dateFn, getDifference } from '../../utils/dateTimeFormatter';
import { textCheckFn } from '../../utils/commonUtil';
import { titleCaseFormatter } from '../../utils/stringFormatters';
import { generateInitials } from '../../components/Utils';

// Components
import OrderPreferencesModule from '../../components/OrderPreferencesModule';

// Images
import no_picture from '../../images/no-profile-picture.png';

// ─── localStorage helper (used to clear draft on success) ───────────────────────
const LS_KEY = 'createOrderV2_draft';
function clearDraft() {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {}
}

export default function StepReview({ onOrderConfirmed }) {
  const dispatch = useDispatch();
  const tenantName = import.meta.env.VITE_TENANT_NAME;
  const {
    orderType,
    orderTimeSlots,
    orderServiceAddress,
    orderProvider,
    orderPreferences,
    orderRepeatFrequency,
    orderPaymentDetails,
    selectedServices,
    orderPromoCode,
    final_tip,
  } = useSelector((s) => s.createNewOrder);
  const [tipAmount, setTipAmount] = useState(final_tip ?? '');
  const [tipError, setTipError] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const saveTip = () => {
    const val = tipAmount === '' ? null : +tipAmount;
    if (val !== null && (val < 1 || val > 500)) {
      setTipError(true);
      return;
    }
    setTipError(false);
    dispatch(setOrderDataAction({ final_tip: val }));
    toast.success('Tip saved');
  };

  const placeOrder = () => {
    setProcessing(true);
    setErrorMsg('');
    if (orderType === 'oneTime') {
      const payload = {
        service_address_id: orderServiceAddress?.id,
        items: selectedServices?.map((i) => ({ service_id: i.service_id, quantity: i.quantity })),
        preferred_worker_id: orderProvider?.id || undefined,
        pickup_date: orderTimeSlots?.pickup_date,
        dropoff_date: orderTimeSlots?.dropoff_date,
        pickup_start_time: orderTimeSlots?.pickup_start_time,
        pickup_end_time: orderTimeSlots?.pickup_end_time,
        dropoff_start_time: orderTimeSlots?.dropoff_start_time,
        dropoff_end_time: orderTimeSlots?.dropoff_end_time,
        preferences: orderPreferences || undefined,
        promo_code: orderPromoCode,
        create_order: true,
        ...(final_tip && { final_tip }),
      };
      createNewOrder(payload, 'ORDER').then((res) => {
        setProcessing(false);
        if (res?.status === 200 || res?.status === 201) {
          toast.success(res?.message || 'Order created!');
          dispatch(setOrderDataAction({ newOrderId: res?.order_id || null }));
          clearDraft();
          onOrderConfirmed();
        } else {
          setErrorMsg(res?.message || 'Something went wrong');
          toast.error(res?.message || 'Something went wrong');
        }
      });
    } else {
      const payload = {
        service_address_id: orderServiceAddress?.id,
        items: selectedServices?.map((i) => ({ service_id: i.service_id, quantity: i.quantity })),
        preferred_worker_id: orderProvider?.id || undefined,
        subscription_entries: {
          first_pickup_date: `${orderTimeSlots?.first_pickup_date}T00:00:00`,
          pickup_start_time: orderTimeSlots?.pickup_start_time,
          pickup_end_time: orderTimeSlots?.pickup_end_time,
          dropoff_start_time: orderTimeSlots?.dropoff_start_time,
          dropoff_end_time: orderTimeSlots?.dropoff_end_time,
          subscription_frequency: orderRepeatFrequency?.value,
          days_after: getDifference(orderTimeSlots?.first_pickup_date, orderTimeSlots?.dropoff_date),
        },
        preferences: orderPreferences || undefined,
        promo_code: orderPromoCode,
        create_subscription: true,
        ...(final_tip && { final_tip }),
      };
      createNewSubscriptions(payload, 'subscription').then((res) => {
        setProcessing(false);
        if (res?.status === 200 || res?.status === 201) {
          toast.success(res?.message || 'Subscription created!');
          dispatch(setOrderDataAction({ newSubscriptionId: res?.subscription_id || null }));
          clearDraft();
          onOrderConfirmed();
        } else {
          setErrorMsg(res?.message || 'Something went wrong');
          toast.error(res?.message || 'Something went wrong');
        }
      });
    }
  };

  const pickupDate = orderType === 'oneTime' ? orderTimeSlots?.pickup_date : orderTimeSlots?.first_pickup_date;
  const pickupStr = pickupDate
    ? dateFn(
        'toLocal',
        pickupDate,
        orderTimeSlots?.pickup_start_time,
        orderTimeSlots?.pickup_end_time,
        orderServiceAddress?.time_zone
      )
    : '';
  const deliveryStr = orderTimeSlots?.dropoff_date
    ? dateFn(
        'toLocal',
        orderTimeSlots.dropoff_date,
        orderTimeSlots.dropoff_start_time,
        orderTimeSlots.dropoff_end_time,
        orderServiceAddress?.time_zone
      )
    : '';

  return (
    <div>
      {/* Summary card */}
      <div className='space-y-3 rounded-lg border border-slate-200 bg-white p-4 text-sm'>
        {/* Address & Provider */}
        <div className='flex gap-6'>
          <div className='flex-1'>
            <p className='text-xs text-slate-500'>Service Address</p>
            <p className='font-semibold text-slate-800'>{orderServiceAddress?.full_address}</p>
          </div>
          <div className='flex-1'>
            <p className='text-xs text-slate-500'>Provider</p>
            <div className='flex items-center gap-2'>
              {orderProvider?.id == null ? (
                <img className='h-8 w-8 rounded-full bg-slate-100' src={no_picture} alt='' />
              ) : orderProvider?.profile_image?.absolute_url ? (
                <img
                  className='h-8 w-8 rounded-full object-cover'
                  src={orderProvider.profile_image.absolute_url}
                  alt=''
                />
              ) : (
                <div className='flex h-8 w-8 items-center justify-center rounded-full bg-pink-400 text-xs font-bold text-white'>
                  {generateInitials(`${orderProvider?.first_name} ${orderProvider?.last_name}`)}
                </div>
              )}
              <span className='font-semibold text-slate-800'>
                {orderProvider?.id == null ? 'Auto-Match' : `${orderProvider?.first_name} ${orderProvider?.last_name}`}
              </span>
            </div>
          </div>
        </div>
        <hr className='border-dashed border-slate-200' />
        {/* Payment */}
        <div>
          <p className='text-xs text-slate-500'>Payment</p>
          <p className='font-semibold text-slate-800'>
            {orderPaymentDetails?.billing_details?.default_payment_method_type === 'STRIPE'
              ? `${titleCaseFormatter(orderPaymentDetails?.billing_details?.default_payment_method?.card_brand)} •••• ${
                  orderPaymentDetails?.billing_details?.default_payment_method?.card_last4
                }`
              : textCheckFn(orderPaymentDetails?.billing_details?.default_payment_method_type)}
          </p>
        </div>
        <hr className='border-dashed border-slate-200' />
        {/* Schedule */}
        {orderType === 'subscription' && (
          <div>
            <p className='text-xs text-slate-500'>Frequency</p>
            <p className='font-semibold text-slate-800'>{orderRepeatFrequency?.name}</p>
          </div>
        )}
        <div className='flex gap-6'>
          <div className='flex-1'>
            <p className='text-xs text-slate-500'>Pickup</p>
            <p className='font-semibold text-slate-800'>{pickupStr}</p>
          </div>
          <div className='flex-1'>
            <p className='text-xs text-slate-500'>Delivery</p>
            <p className='font-semibold text-slate-800'>{deliveryStr}</p>
          </div>
        </div>
        <hr className='border-dashed border-slate-200' />
        {/* Preferences */}
        <div>
          <p className='text-xs text-slate-500'>Preferences</p>
          <div className='mt-1'>
            <OrderPreferencesModule type='view' preferences={orderPreferences} loader={false} />
          </div>
        </div>
      </div>

      {/* Tip */}
      <div className='mt-3 rounded-lg border border-slate-200 bg-white p-4'>
        <p className='mb-2 text-xs font-semibold text-slate-500'>
          {orderProvider?.id ? `Tip for ${orderProvider.first_name}` : 'Tip for provider'}
        </p>
        <div className='flex gap-2'>
          <input
            type='number'
            className={`w-32 rounded border px-3 py-1.5 text-sm ${tipError ? 'border-red-400' : 'border-slate-200'}`}
            placeholder='$0.00'
            value={tipAmount}
            onChange={(e) => {
              setTipAmount(e.target.value);
              setTipError(false);
            }}
          />
          <button className='bg-client-300 rounded px-4 py-1.5 text-xs font-semibold text-white' onClick={saveTip}>
            Save
          </button>
        </div>
        {tipError && <p className='mt-1 text-xs text-red-500'>Tip must be $1 – $500</p>}
      </div>

      {/* Confirm */}
      <div className='mt-4 text-center'>
        <p className='mb-2 text-xs text-slate-500'>
          By confirming you agree to our{' '}
          <a
            href={
              tenantName === 'laundrycare' ? 'https://www.laundrycare.biz/tos/' : 'https://www.pushlaundry.com/tos/'
            }
            target='_blank'
            rel='noopener noreferrer'
            className='text-blue-500 underline'
          >
            Terms of Service
          </a>
        </p>
        {errorMsg && <p className='mb-2 text-sm text-red-500'>{errorMsg}</p>}
        <button
          type='button'
          className='bg-client-text w-full rounded-lg py-3 text-sm font-bold text-white shadow transition hover:opacity-90 disabled:opacity-50'
          onClick={placeOrder}
          disabled={processing}
        >
          {processing ? 'Processing...' : 'Place Order'}
        </button>
      </div>
    </div>
  );
}
